import React, {useEffect, useState} from 'react';
import {ILeaveType} from "./leave-summary.types";
import {firstCharUpperCase} from "../../../../utility/utility";
import LeavePeriodDropdown from "./component/leave-period-dropdown";
import server from "../../../../server";
import MonthLeaveTrendChart from "./charts/month-leave-trend-chart";
import '../../../../../src/styles/component/leave-summary.scss'
import LoadingSkeleton from "../../../../page-components/common/loading-skeleton/loading-skeleton";
import StatCards, {StatCardData} from "../../../../page-components/common/stat-cards/stat-cards";
import {Calendar, ChartPie, Check, Clock} from "lucide-react";

type MonthChartRow = {
    month: string;
    [key: string]: string | number;
};


interface ILeaveSummaryEmployeeProps {
    monthList: string[];
    employeeId: string;
}

interface ISummaryData {
    totalLeaves: number,
    totalBalance: number,
    leaves: ILeaveType[],
    monthChartDataset: any,
    leaveStatus: {
        pending: number,
        approved: number,
        rejected: number,
        cancelled: number
    },
    leavePeriodId: string
}


const LeaveSummaryEmployee = (props: ILeaveSummaryEmployeeProps) => {
    const {monthList, employeeId} = props;
    const [loading, setLoading] = useState(true);
    const initialSummaryData = {
        totalLeaves: 0,
        totalBalance: 0,
        leaveStatus: {
            approved: 0,
            rejected: 0,
            pending: 0,
            cancelled: 0
        },
        leaves: [],
        monthChartDataset: [],
        leavePeriodId: ''
    }
    const [summaryData, setSummaryData] = useState<ISummaryData>(initialSummaryData);

    useEffect(() => {
        (async () => {
            if (summaryData.leavePeriodId)
                await getLeaveBalance();
        })();
    }, [employeeId, summaryData.leavePeriodId]);

    function handleLeavePeriodId(val: string) {
        setSummaryData((prev) => ({
            ...prev,
            leavePeriodId: val
        }));
    }

    async function getLeaveBalance() {
        setLoading(true);
        try {
            const payload = {
                employeeId: employeeId,
                leavePeriodId: summaryData.leavePeriodId
            }
            const response = await server.leave.balance.getById(payload);
            if (response.length > 0) {
                const leaveBalance = response[0];
                setSummaryData((prev) => ({
                    ...prev,
                    totalLeaves: leaveBalance.unit,
                    totalBalance: leaveBalance.unit - (leaveBalance.pending + leaveBalance.approved),
                    leaves: leaveBalance.leaves,
                    leaveStatus: {
                        pending: leaveBalance.pending,
                        approved: leaveBalance.approved,
                        rejected: leaveBalance.rejected,
                        cancelled: leaveBalance.cancelled
                    }
                }));
                getMonthlyLeaveData(leaveBalance.leaves);
            } else {
                setSummaryData({...initialSummaryData, leavePeriodId: summaryData.leavePeriodId});
            }

        } catch (e) {
            console.log('error occured', e)
        } finally {
            setLoading(false)
        }
    }

    function getMonthlyLeaveData(leaves: ILeaveType[]) {

        const chartData: MonthChartRow[] = monthList.map(month => ({
            month
        }));

        leaves.forEach(leave => {
            leave.months.forEach((m, index) => {
                chartData[index][leave.type] = m.applied ?? 0;
            });
        });

        setSummaryData(prev => ({
            ...prev,
            monthChartDataset: chartData
        }));
    }

    const getLeaveSummaryStatsCards = (data: ISummaryData): StatCardData[] => {
        const totalLeavesTaken = data.leaves.reduce(
            (sum, item) => sum + (item.applied || 0),
            0 // initial value
        );

        const usagePercentage =
            data.totalLeaves > 0
                ? Number(((totalLeavesTaken / data.totalLeaves) * 100).toFixed(1))
                : 0;

        return [
            {
                key: "totalLeaves",
                label: "Total Alloted",
                value: data.totalLeaves,
                icon: Calendar,
                color: "default",
            },

            {
                key: "totalBalance",
                label: "Available Balance",
                value: data.totalBalance,
                icon: Check,
                color: "green",
            },

            {
                key: "applied",
                label: "Leaves Taken",
                value: data.leaves.map((item: ILeaveType) => (
                    item.applied
                )),
                icon: Clock,
                color: "orange",
            },
            {
                key: "usage",
                label: "Leaves Used",
                value: `${usagePercentage}%`,
                icon: ChartPie,
                color: "pink",
            },
        ];
    };


    return (
        <section className={'leave-summary-employee'}>
            <LeavePeriodDropdown setLeavePeriod={handleLeavePeriodId}/>

            <div className={'employee-leave-summary-charts'}>
                <StatCards data={getLeaveSummaryStatsCards(summaryData)} size={'small'} loading={loading}/>
            </div>

            <div className={'employee-leave-summary-graph-container'}>
                <LoadingSkeleton loading={loading}>
                    <MonthLeaveTrendChart summary={summaryData.monthChartDataset}/>
                </LoadingSkeleton>

                <LoadingSkeleton loading={loading}>
                    <div className="employee-leave-summary__leave-status">
                        <h3 className="title">Leave Requests</h3>
                        <ul className="list">
                            {Object.entries(summaryData.leaveStatus).map(
                                ([key, value]: any, index: number) => (
                                    <li key={`${key}-${index}`}>
                                        <div className="status-info">
                                            <span className={`employee-leave-status-badge ${key}`}/>
                                            <label>{firstCharUpperCase(key)}</label>
                                        </div>
                                        <span className={`count ${key}`}>{value}</span>
                                    </li>
                                )
                            )}
                        </ul>
                    </div>
                </LoadingSkeleton>
            </div>
        </section>
    );
}

export default LeaveSummaryEmployee;